<template>
  <div class="report-wrapper">
    <ReportNavbar
      :search-report-value="searchReportValue"
      :selected-filter="selectedFilter"
      :row-list="selectedRowList"
      @update-selected-filter="updateSelectedFilter"
      @update-search-report-value="updateSearchReportValue"
      @delete-row="isShowDeleteReportModal = true;"
      @download-row="downloadRow(selectedRowList)"
      @upload-file="file => uploadFile(file)"
    />

    <div class="report-table">
      <div
        :class="{'report-table-header_use-scroll': useScroll}"
        class="report-table-header"
      >
        <div class="report-table-header__column">
          <AvalCheckbox
            :model-value="selectedRowList.length > 0"
            @click="toggleAllRows"
          />
        </div>
        <div class="report-table-header__column">
          {{ t("analytics_reports.column.title") }}
        </div>
        <div class="report-table-header__column" />
        <div
          :class="{'report-table-header__column_use-scroll': useScroll}"
          class="report-table-header__column"
        >
          {{ t("analytics_reports.column.published_date") }}
        </div>
      </div>

      <div
        :class="{'report-table-body_no-scroll': !useScroll}"
        class="report-table-body"
      >
        <div
          :class="{'report-table-rows_no-scroll': !useScroll}"
          class="report-table-rows table-scroll"
        >
          <div
            v-for="row in reportFileList"
            :key="row"
            class="report-table-rows__row"
            @mouseenter="currentRowHover = row"
            @mouseleave="currentRowHover = null"
          >
            <div class="report-table-rows__column">
              <AvalCheckbox
                :model-value="selectedRowList.includes(row)"
                @click="toggleRow(row)"
              />
            </div>

            <div class="report-table-rows__column">
              {{ row.originalFilename }}
            </div>
            <div class=" report-table-rows__control">
              <AvalButtonIconNeutralSecondary
                v-if="currentRowHover === row"
                @click="downloadRow([row])"
              >
                <DownloadIcon />
              </AvalButtonIconNeutralSecondary>
              <AvalButtonIconNeutralSecondary
                v-if="currentRowHover === row"
                @click="toggleDeleteRowModal(row)"
              >
                <TrashIcon />
              </AvalButtonIconNeutralSecondary>
            </div>

            <div class="report-table-rows__column">
              {{ formatDate(row.createdAt) }}
            </div>
          </div>
        </div>

        <div
          v-if="useScroll"
          class="empty"
        />
      </div>
    </div>
  </div>

  <AvalModal 
    v-if="isShowDeleteReportModal"
    :title="t('analytics_reports.modal.delete.title')"
    :use-cross-icon="true" 
    teleport="#app"
    @close="closeModal"
    @confirm="deleteRow"
    @cancel="closeModal" 
  >
    {{ t('analytics_reports.modal.delete.message') }}
  </AvalModal>
</template>

<script setup>
import ReportNavbar from "@/components/AnalyticsPage/AnalyticsReports/ReportNavbar.vue";
import { computed, onMounted, ref } from "vue";
import {useReportStore} from "@/store/Analytic/report-store";
import DownloadIcon from "@/components/Icons/DownloadIcon.vue";
import TrashIcon from "@/components/Icons/TrashIcon.vue";
import { t } from "@/controllerLK/AvalLocalization";

onMounted(async () => {
  await reportStore.getReportFiles();
});

const reportStore = useReportStore();

const selectedFilter = ref(0);
const searchReportValue = ref("");

const isShowDeleteReportModal = ref(false);
const currentRowHover = ref(null);
const selectedRowList = ref([]);
const selectedRow = ref(null);

const useScroll = computed(() => reportStore.reportList.length >= 8);

const reportFileList = computed(() => {
  if (searchReportValue.value.length > 0) {
    return sortReportFiles(reportStore.reportList.filter((report) => report.originalFilename.toLowerCase().includes(searchReportValue.value.toLowerCase())), selectedFilter.value);
  } else {
    return sortReportFiles(reportStore.reportList, selectedFilter.value);
  }
});

function sortReportFiles(files, filterValue) {
  return files.slice().sort((a, b) => {
    switch (filterValue) {
      case 0: // Сначала новые
        return new Date(b.createdAt) - new Date(a.createdAt);
      case 1: // Сначала старые
        return new Date(a.createdAt) - new Date(b.createdAt);
      case 2: // По названию
        return a.filename.localeCompare(b.filename);
      default:
        return 0;
    }
  });
}

const toggleAllRows = () => {
  selectedRowList.value.length > 0 ? selectedRowList.value = [] : selectedRowList.value = reportStore.reportList;
};

const toggleRow = (row) => {
  selectedRowList.value.includes(row) ? selectedRowList.value.splice(selectedRowList.value.indexOf(row), 1) : selectedRowList.value.push(row);
};

const toggleDeleteRowModal = (row) => {
  if (!Array.isArray(row)) {
    selectedRow.value = row;
  }

  isShowDeleteReportModal.value = !isShowDeleteReportModal.value;
};

const deleteRow = async () => {
  if (selectedRow.value) {
    await reportStore.deleteFile(selectedRow.value.filename);
    selectedRow.value = null;
  } else {
    for (const item of selectedRowList.value) {
      await reportStore.deleteFile(item.filename);
    }
    selectedRowList.value = [];
  }
  await reportStore.getReportFiles();
  isShowDeleteReportModal.value = false;
};

const downloadRow = async (rowList) => {
  for (const item of rowList) {
    await reportStore.downloadFile(item.filename);
  }
};

const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append("files", file);
  await reportStore.uploadFile(formData);
  await reportStore.getReportFiles();
};

const formatDate = (isoString) => {
  const date = new Date(isoString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Месяцы начинаются с 0
  const year = date.getFullYear();

  return `${day}.${month}.${year}`;
};

const closeModal = () => {
  isShowDeleteReportModal.value = false;
  if (selectedRow.value && typeof selectedRow.value === "object" && "value" in selectedRow.value) {
    selectedRow.value = null;
  } else {
    selectedRow.value = null;
  }
};

const updateSelectedFilter = (val) => {
  selectedFilter.value = val;
};

const updateSearchReportValue = (val) => {
  searchReportValue.value = val;
};
</script>

<style lang="scss" scoped>
.report-wrapper {
  display: flex;
  flex-direction: column;
  margin-top: 14px;
  padding: 24px;
  border-radius: 10px;
  background: var(--background-bg-white);
  gap: 16px;

  .report-table {

    &-body {
      display: grid;
      justify-content: space-between;
      grid-template-columns: calc(100% - 15px) 15px;

      &_no-scroll {
        grid-template-columns: 100%;
      }

      .empty {
        border-right: 1px solid #EEF1FF;
        border-bottom: 1px solid #EEF1FF;
      }
    }

    &-rows {
      max-height: 600px;
      padding-right: 15px;
      border-left: 1px solid #EEF1FF;
      border-bottom: 1px solid #EEF1FF;

      &_no-scroll {
        padding-right: 0;
        border-right: 1px solid #EEF1FF;
      }

      &__control {
        display: flex;
        gap: 10px;
      }

      &__row {
        height: 80px;
        align-items: center;
        display: grid;
        padding: 16px 16px 16px 16px;
        border: 1px solid #EEF1FF;
        grid-template-columns: 40px auto 100px 120px;
        gap: 10px;

        &:hover {
          background: var(--background-bg-hover);
        }
      }
    }

    &-header {
      display: grid;
      align-items: center;
      padding: 16px;
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
      background: var(--background-01);
      grid-template-columns: 40px auto 100px 120px;
      gap: 10px;

      &_use-scroll {
        padding-right: 64px;
      }


      &__column {
        font-size: 14px;
        font-weight: 500;
        line-height: 22px;
      }
    }
  }

}

.table-scroll {
  overflow-y: auto;
  // Стили для скрола
  &::-webkit-scrollbar {

    width: 14px; /* width of the entire scrollbar */
  }

  &::-webkit-scrollbar-track {
    margin-right: 50px;
    padding-right: 50px;
    background: transparent; /* color of the tracking area */
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 30px; /* roundness of the scroll thumb */
    background-color: #1A64D433; /* color of the scroll thumb */
  }

  &_bg-light {
    &::-webkit-scrollbar-thumb {
      background: var(--background-01);
    }
  }
}
</style>
